import { useState, useRef, useCallback, useMemo } from "react";
import Editor from "@monaco-editor/react";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import {
  Play,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Loader2,
  Terminal,
  FileCode2,
  FolderOpen,
  Folder,
  FileText,
  ChevronRight,
  ChevronDown,
  Send,
  BookOpen,
  FlaskConical,
  StickyNote,
} from "lucide-react";
import { VoiceRecorder } from "./VoiceRecorder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── helpers ──────────────────────────────────────────────── */

function buildFileTree(files) {
  const root = { name: "", children: {}, files: [] };
  for (const f of files) {
    const parts = f.path.split("/");
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!node.children[parts[i]]) {
        node.children[parts[i]] = { name: parts[i], children: {}, files: [] };
      }
      node = node.children[parts[i]];
    }
    node.files.push(f);
  }
  return root;
}

function flattenTree(node, depth = 0) {
  const entries = [];
  const dirs = Object.values(node.children).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const files = [...node.files].sort((a, b) => {
    const aName = a.path.split("/").pop();
    const bName = b.path.split("/").pop();
    return aName.localeCompare(bName);
  });
  for (const dir of dirs) {
    entries.push({ type: "dir", name: dir.name, depth, node: dir });
    entries.push(...flattenTree(dir, depth + 1));
  }
  for (const file of files) {
    entries.push({
      type: "file",
      name: file.path.split("/").pop(),
      path: file.path,
      language: file.language,
      editable: file.editable !== false,
      depth,
    });
  }
  return entries;
}

/* ─── Language mapping (THCO Schema Contract) ──────────────── */

const MONACO_LANG_MAP = {
  java: "java",
  python: "python",
  csharp: "csharp",
  javascript: "javascript",
  typescript: "typescript",
  sql: "sql",
  bash: "shell",
  yaml: "yaml",
  json: "json",
  xml: "xml",
  markdown: "markdown",
  properties: "plaintext",
};

function toMonacoLang(lang) {
  return MONACO_LANG_MAP[lang] || "plaintext";
}

const LANG_ICONS = {
  java: "J", python: "Py", csharp: "C#", javascript: "JS", typescript: "TS",
  sql: "SQL", bash: "SH", yaml: "Y", json: "{}", xml: "<>", markdown: "MD",
  properties: "P",
};

const LANG_COLORS = {
  java: "text-orange-400", python: "text-yellow-400", csharp: "text-green-400",
  javascript: "text-amber-400", typescript: "text-blue-400", sql: "text-cyan-400",
  bash: "text-lime-400", yaml: "text-pink-400", json: "text-violet-400",
  xml: "text-rose-400", markdown: "text-blue-400", properties: "text-gray-400",
};

function langIcon(lang) {
  return LANG_ICONS[lang] || "F";
}

function langColor(lang) {
  return LANG_COLORS[lang] || "text-gray-400";
}

function getPrimaryLanguage(files) {
  const editableSource = files.filter(
    (f) => f.editable !== false && f.path !== "NOTES.md"
  );
  if (editableSource.length === 0) return "plaintext";
  // Most common language among editable files
  const counts = {};
  for (const f of editableSource) {
    counts[f.language] = (counts[f.language] || 0) + 1;
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
}

/* ─── component ────────────────────────────────────────────── */

export function SandboxSimulation({
  item,
  index,
  responses,
  onResponseChange,
  candidateId,
  voiceEnabled,
  token,
  sectionId,
  activityLogger,
}) {
  const id = item.id || `sandbox_${index}`;
  const title = item.title || "Sandbox Simulation";
  const files = item.files || [];
  const readme = item.readme || "";
  const testSuite = item.test_suite || {};
  const baselineResults = testSuite.baseline_results || [];
  const diagConsole = item.diagnostic_console || {};
  const availableCommands = diagConsole.available_commands || [];
  const maxTestRuns = item.max_test_runs || 10;

  // Determine first editable non-notes file as default
  const defaultFile = useMemo(() => {
    const first = files.find(
      (f) => f.editable !== false && f.path !== "NOTES.md"
    );
    return first ? first.path : files[0]?.path || "";
  }, [files]);

  const notesFile = useMemo(
    () => files.find((f) => f.path === "NOTES.md" || f.path.endsWith("/NOTES.md")),
    [files]
  );
  const notesPath = notesFile?.path;

  const primaryLang = useMemo(() => getPrimaryLanguage(files), [files]);

  // State
  const [activeFile, setActiveFile] = useState(defaultFile);
  const [fileContents, setFileContents] = useState(() => {
    const map = {};
    for (const f of files) {
      const savedKey = `${id}__file__${f.path}`;
      map[f.path] = responses[savedKey] !== undefined ? responses[savedKey] : f.content;
    }
    return map;
  });
  const [originalContents] = useState(() => {
    const map = {};
    for (const f of files) map[f.path] = f.content;
    return map;
  });
  const [testRunsUsed, setTestRunsUsed] = useState(
    parseInt(responses[`${id}__test_runs`] || "0", 10)
  );
  const [testResults, setTestResults] = useState(null);
  const [testRunHistory, setTestRunHistory] = useState([]);
  const [showBaseline, setShowBaseline] = useState(true);
  const [running, setRunning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(
    responses[`${id}__submitted`] === "true"
  );
  const [consoleHistory, setConsoleHistory] = useState([]);
  const [navigationHistory, setNavigationHistory] = useState([]);
  const [rightTab, setRightTab] = useState("readme");
  const [collapsedDirs, setCollapsedDirs] = useState(new Set());
  const editorRef = useRef(null);

  // Active file metadata
  const activeFileMeta = useMemo(
    () => files.find((f) => f.path === activeFile),
    [files, activeFile]
  );
  const activeLang = activeFileMeta?.language || "plaintext";
  const activeEditable = activeFileMeta?.editable !== false && !submitted;
  const activeContent = fileContents[activeFile] || "";

  // File tree (exclude NOTES.md which gets its own section)
  const treeEntries = useMemo(() => {
    const tree = buildFileTree(files.filter((f) => f.path !== notesPath));
    return flattenTree(tree);
  }, [files, notesPath]);

  /* ─── handlers ─────────────────────────────────────────── */

  const handleEditorChange = useCallback(
    (value) => {
      const newVal = value || "";
      setFileContents((prev) => ({ ...prev, [activeFile]: newVal }));
      onResponseChange(`${id}__file__${activeFile}`, newVal);
    },
    [activeFile, id, onResponseChange]
  );

  const handleNotesChange = useCallback(
    (value) => {
      const newVal = value || "";
      setFileContents((prev) => ({ ...prev, [notesPath || "NOTES.md"]: newVal }));
      onResponseChange(`${id}__file__${notesPath || "NOTES.md"}`, newVal);
      activityLogger?.logNotesEdit(newVal.length);
    },
    [id, onResponseChange, notesPath, activityLogger]
  );

  const handleResetFile = () => {
    const orig = originalContents[activeFile];
    if (orig !== undefined) {
      setFileContents((prev) => ({ ...prev, [activeFile]: orig }));
      onResponseChange(`${id}__file__${activeFile}`, orig);
    }
  };

  const toggleDir = (dirName) => {
    setCollapsedDirs((prev) => {
      const next = new Set(prev);
      next.has(dirName) ? next.delete(dirName) : next.add(dirName);
      return next;
    });
  };

  const handleRunTests = async () => {
    if (testRunsUsed >= maxTestRuns) return;
    setRunning(true);
    setRightTab("tests");
    setShowBaseline(false);

    const newCount = testRunsUsed + 1;
    setTestRunsUsed(newCount);
    onResponseChange(`${id}__test_runs`, String(newCount));
    activityLogger?.logTestRun({ run_number: newCount, max_runs: maxTestRuns });

    // Build diff payload
    const changedFiles = {};
    for (const f of files) {
      if (f.path === notesPath) continue;
      changedFiles[f.path] = {
        original: originalContents[f.path] || "",
        modified: fileContents[f.path] || "",
        language: f.language,
      };
    }

    try {
      const res = await fetch(`${API}/ta/simulation/evaluate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidate_id: candidateId,
          simulation_id: id,
          code: JSON.stringify(changedFiles),
          language: primaryLang,
          test_cases: baselineResults.map((t) => ({
            description: t.name || t.test_name || "",
            input: "",
            expected_output: t.status === "PASS" ? "PASS" : "Fixed",
          })),
          mode: "run",
        }),
      });
      const data = await res.json();
      if (res.ok) {
        const results = data.test_results || [];
        setTestResults(results);
        // Capture file states at time of this run (only modified files)
        const fileStatesAtRun = {};
        for (const f of files) {
          const current = fileContents[f.path] || "";
          const original = originalContents[f.path] || "";
          if (current !== original) {
            fileStatesAtRun[f.path] = current;
          }
        }
        setTestRunHistory((prev) => [
          ...prev,
          {
            run_number: newCount,
            timestamp: new Date().toISOString(),
            results,
            file_states_at_run: fileStatesAtRun,
          },
        ]);
      } else {
        setTestResults([
          {
            description: "Evaluation Error",
            passed: false,
            message: data.detail || "Failed to evaluate",
          },
        ]);
      }
    } catch (err) {
      setTestResults([
        {
          description: "Network Error",
          passed: false,
          message: err.message,
        },
      ]);
    } finally {
      setRunning(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const fileDiffs = {};
    for (const f of files) {
      fileDiffs[f.path] = {
        original: originalContents[f.path] || "",
        modified: fileContents[f.path] || "",
        language: f.language,
        editable: f.editable !== false,
        changed: (fileContents[f.path] || "") !== (originalContents[f.path] || ""),
      };
    }

    const codePayload = {};
    for (const f of files) {
      if (f.path === notesPath) continue;
      codePayload[f.path] = {
        original: originalContents[f.path] || "",
        modified: fileContents[f.path] || "",
        language: f.language,
      };
    }

    try {
      const res = await fetch(`${API}/ta/simulation/evaluate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          candidate_id: candidateId,
          simulation_id: id,
          code: JSON.stringify(codePayload),
          language: primaryLang,
          test_cases: baselineResults.map((t) => ({
            description: t.name || t.test_name || "",
            input: "",
            expected_output: "PASS",
          })),
          mode: "submit",
          notes: fileContents[notesFile?.path || "NOTES.md"] || "",
          test_run_history: testRunHistory,
          console_history: consoleHistory,
          file_diffs: fileDiffs,
          navigation_history: navigationHistory,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setTestResults(data.test_results || []);
        setSubmitted(true);
        onResponseChange(`${id}__submitted`, "true");
        onResponseChange(`${id}__score`, String(data.score || 0));
        setRightTab("tests");
        setShowBaseline(false);
      }
    } catch (err) {
      /* noop */
    } finally {
      setSubmitting(false);
    }
  };

  const handleConsoleCommand = (cmd) => {
    setConsoleHistory((prev) => [
      ...prev,
      { command: cmd.command, response: cmd.response },
    ]);
    setRightTab("console");
    activityLogger?.logConsoleCommand(cmd.command);
  };

  /* ─── derived ──────────────────────────────────────────── */
  const modifiedCount = files.filter(
    (f) =>
      f.path !== notesPath && fileContents[f.path] !== originalContents[f.path]
  ).length;

  const passedTests = testResults
    ? testResults.filter((t) => t.passed).length
    : 0;
  const totalTests = testResults ? testResults.length : 0;

  /* ─── render ───────────────────────────────────────────── */
  return (
    <div
      className="my-6 border border-[#2a2a3e] rounded-lg overflow-hidden bg-[#1e1e2e]"
      data-testid={`sandbox-simulation-${index}`}
    >
      {/* ── Header ───────────────────────────────────────── */}
      <div className="flex items-center justify-between bg-[#12121e] px-4 py-2.5 border-b border-[#2a2a3e]">
        <div className="flex items-center gap-3">
          <FileCode2 className="w-4 h-4 text-emerald-400" />
          <span className="text-sm font-semibold text-gray-200">
            {title}
          </span>
          <span className="text-xs bg-orange-900/40 text-orange-300 px-2 py-0.5 rounded uppercase tracking-wide">
            {primaryLang}
          </span>
          {modifiedCount > 0 && (
            <span className="text-xs bg-blue-900/40 text-blue-300 px-2 py-0.5 rounded">
              {modifiedCount} file{modifiedCount > 1 ? "s" : ""} modified
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 mr-1">
            {maxTestRuns - testRunsUsed} runs left
          </span>
          <Button
            size="sm"
            onClick={handleRunTests}
            disabled={
              running || submitting || submitted || testRunsUsed >= maxTestRuns
            }
            className="h-7 px-3 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="sandbox-run-tests"
          >
            {running ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5 mr-1" />
            )}
            {running ? "Running..." : "Run Tests"}
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={running || submitting || submitted}
            className="h-7 px-3 text-xs bg-[#00AEEF] hover:bg-[#0090c5] text-white"
            data-testid="sandbox-submit"
          >
            {submitting ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5 mr-1" />
            )}
            {submitted ? "Submitted" : "Submit"}
          </Button>
        </div>
      </div>

      {/* ── 3-Panel Body ─────────────────────────────────── */}
      <div
        className="grid"
        style={{ gridTemplateColumns: "200px 1fr 320px", height: "520px" }}
      >
        {/* Left: File Explorer */}
        <div
          className="border-r border-[#2a2a3e] overflow-y-auto bg-[#181825]"
          data-testid="sandbox-file-explorer"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
            Explorer
          </div>
          {treeEntries.map((entry, i) => {
            if (entry.type === "dir") {
              const isCollapsed = collapsedDirs.has(entry.name);
              return (
                <button
                  key={`dir-${i}`}
                  onClick={() => toggleDir(entry.name)}
                  className="w-full flex items-center gap-1.5 px-2 py-1 text-xs text-gray-400 hover:bg-[#2a2a3e] transition-colors"
                  style={{ paddingLeft: `${entry.depth * 12 + 8}px` }}
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-3 h-3 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-3 h-3 flex-shrink-0" />
                  )}
                  {isCollapsed ? (
                    <Folder className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" />
                  ) : (
                    <FolderOpen className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                  )}
                  <span className="truncate">{entry.name}</span>
                </button>
              );
            }

            // Check if parent dir is collapsed
            const parentDirIdx = (() => {
              for (let k = i - 1; k >= 0; k--) {
                if (
                  treeEntries[k].type === "dir" &&
                  treeEntries[k].depth < entry.depth
                ) {
                  return collapsedDirs.has(treeEntries[k].name);
                }
              }
              return false;
            })();
            if (parentDirIdx) return null;

            const isActive = entry.path === activeFile;
            const isModified =
              fileContents[entry.path] !== originalContents[entry.path];
            return (
              <button
                key={entry.path}
                onClick={() => {
                  setActiveFile(entry.path);
                  activityLogger?.logFileOpened(entry.path);
                  if (!submitted) {
                    setNavigationHistory((prev) => [
                      ...prev,
                      { file: entry.path, opened_at: new Date().toISOString() },
                    ]);
                  }
                }}
                className={`w-full flex items-center gap-1.5 px-2 py-1 text-xs transition-colors ${
                  isActive
                    ? "bg-[#2a2a3e] text-white"
                    : "text-gray-400 hover:bg-[#2a2a3e]/50"
                }`}
                style={{ paddingLeft: `${entry.depth * 12 + 20}px` }}
                data-testid={`file-${entry.name}`}
              >
                <span
                  className={`w-4 h-4 flex-shrink-0 flex items-center justify-center text-[9px] font-bold rounded ${langColor(
                    entry.language
                  )}`}
                >
                  {langIcon(entry.language)}
                </span>
                <span className="truncate">{entry.name}</span>
                {isModified && (
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0 ml-auto" />
                )}
                {!entry.editable && (
                  <span className="text-[9px] text-gray-600 ml-auto">RO</span>
                )}
              </button>
            );
          })}
          {/* NOTES.md at bottom */}
          {notesFile && (
            <>
              <div className="border-t border-[#2a2a3e] mt-2 pt-1 px-3 py-1">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                  Notes
                </span>
              </div>
              <button
                onClick={() => { setActiveFile(notesFile.path); activityLogger?.logFileOpened("NOTES.md"); }}
                className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-xs transition-colors ${
                  activeFile === notesFile.path
                    ? "bg-[#2a2a3e] text-white"
                    : "text-gray-400 hover:bg-[#2a2a3e]/50"
                }`}
                data-testid="file-NOTES.md"
              >
                <StickyNote className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
                <span>NOTES.md</span>
              </button>
            </>
          )}
        </div>

        {/* Center: Monaco Editor */}
        <div className="flex flex-col border-r border-[#2a2a3e]">
          {/* Editor tab bar */}
          <div className="flex items-center justify-between bg-[#12121e] px-3 py-1.5 border-b border-[#2a2a3e]">
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <FileCode2 className="w-3.5 h-3.5" />
              <span className="text-gray-300 font-medium">
                {activeFile ? activeFile.split("/").pop() : "No file"}
              </span>
              {!activeEditable && (
                <span className="text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded">
                  READ ONLY
                </span>
              )}
            </div>
            {activeEditable && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFile}
                className="h-6 px-2 text-[10px] text-gray-500 hover:text-white hover:bg-[#2a2a3e]"
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reset File
              </Button>
            )}
          </div>
          <div className="flex-1" data-testid="sandbox-editor">
            <Editor
              height="100%"
              language={toMonacoLang(activeLang)}
              value={activeContent}
              onChange={
                activeFile === (notesFile?.path || "NOTES.md") ? handleNotesChange : handleEditorChange
              }
              theme="vs-dark"
              options={{
                minimap: { enabled: false },
                fontSize: 13,
                lineNumbers: "on",
                scrollBeyondLastLine: false,
                wordWrap: "on",
                tabSize: 4,
                insertSpaces: true,
                automaticLayout: true,
                readOnly: !activeEditable,
                padding: { top: 8, bottom: 8 },
              }}
            />
          </div>
        </div>

        {/* Right: README / Tests / Console */}
        <div className="flex flex-col overflow-hidden bg-[#181825]">
          {/* Tabs */}
          <div className="flex border-b border-[#2a2a3e] bg-[#12121e]">
            {[
              { key: "readme", icon: BookOpen, label: "README" },
              { key: "tests", icon: FlaskConical, label: "Tests" },
              { key: "console", icon: Terminal, label: "Console" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setRightTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                  rightTab === tab.key
                    ? "text-white border-b-2 border-emerald-400 bg-[#1e1e2e]"
                    : "text-gray-500 hover:text-gray-300"
                }`}
                data-testid={`tab-${tab.key}`}
              >
                <tab.icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto">
            {/* README */}
            {rightTab === "readme" && (
              <div
                className="p-4 text-sm text-gray-300 prose prose-invert prose-sm max-w-none
                  prose-headings:text-gray-200 prose-h2:text-base prose-h3:text-sm
                  prose-p:text-gray-400 prose-li:text-gray-400
                  prose-strong:text-gray-200 prose-code:text-emerald-400
                  prose-code:bg-[#2a2a3e] prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                  prose-ol:list-decimal prose-ul:list-disc"
                data-testid="sandbox-readme"
              >
                <ReactMarkdown>{readme}</ReactMarkdown>
              </div>
            )}

            {/* Tests */}
            {rightTab === "tests" && (
              <div className="p-3" data-testid="sandbox-test-panel">
                {/* Baseline results */}
                {showBaseline && !testResults && (
                  <>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                      Baseline Test Results
                    </div>
                    <div className="space-y-1">
                      {baselineResults.map((t, i) => (
                        <div
                          key={i}
                          className={`flex items-start gap-2 text-xs px-2.5 py-1.5 rounded ${
                            t.status === "PASS"
                              ? "bg-emerald-950/30 text-emerald-400"
                              : "bg-red-950/30 text-red-400"
                          }`}
                        >
                          {t.status === "PASS" ? (
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          )}
                          <div>
                            <span className="font-medium">
                              {t.name || t.test_name || `Test ${i + 1}`}
                            </span>
                            {t.message && (
                              <p className="text-[11px] opacity-80 mt-0.5">
                                {t.message}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* AI evaluation results */}
                {testResults && (
                  <>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                      Evaluation Results ({passedTests}/{totalTests} passed)
                    </div>
                    <div className="space-y-1">
                      {testResults.map((t, i) => (
                        <div
                          key={i}
                          className={`flex items-start gap-2 text-xs px-2.5 py-1.5 rounded ${
                            t.passed
                              ? "bg-emerald-950/30 text-emerald-400"
                              : "bg-red-950/30 text-red-400"
                          }`}
                          data-testid={`test-result-${i}`}
                        >
                          {t.passed ? (
                            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          )}
                          <div>
                            <span className="font-medium">
                              {t.description || `Test ${i + 1}`}
                            </span>
                            {t.message && (
                              <p className="text-[11px] opacity-80 mt-0.5">
                                {t.message}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Run Tests button (in panel) */}
                <div className="mt-3 pt-3 border-t border-[#2a2a3e]">
                  <Button
                    size="sm"
                    onClick={handleRunTests}
                    disabled={
                      running ||
                      submitting ||
                      submitted ||
                      testRunsUsed >= maxTestRuns
                    }
                    className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="sandbox-run-tests-panel"
                  >
                    {running ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <FlaskConical className="w-3.5 h-3.5 mr-1" />
                    )}
                    {running
                      ? "Evaluating..."
                      : `Run Tests (${maxTestRuns - testRunsUsed} remaining)`}
                  </Button>
                  {submitted && (
                    <div className="flex items-center gap-2 text-xs text-emerald-400 mt-2 px-2 py-1.5 bg-emerald-950/30 rounded">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Solution submitted
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Diagnostic Console */}
            {rightTab === "console" && (
              <div className="p-3" data-testid="sandbox-console">
                <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                  Diagnostic Commands
                </div>
                <div className="space-y-1 mb-3">
                  {availableCommands.map((cmd, i) => (
                    <button
                      key={i}
                      onClick={() => handleConsoleCommand(cmd)}
                      className="w-full text-left flex items-center gap-2 text-xs px-2.5 py-1.5 rounded bg-[#2a2a3e]/50 text-gray-400 hover:bg-[#2a2a3e] hover:text-emerald-400 transition-colors font-mono"
                      data-testid={`console-cmd-${i}`}
                    >
                      <span className="text-emerald-500">$</span>
                      {cmd.command}
                    </button>
                  ))}
                </div>

                {/* Console output */}
                {consoleHistory.length > 0 && (
                  <div className="border-t border-[#2a2a3e] pt-2">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                      Output
                    </div>
                    <div className="space-y-2 font-mono text-xs">
                      {consoleHistory.map((entry, i) => (
                        <div key={i}>
                          <div className="text-emerald-400">
                            $ {entry.command}
                          </div>
                          <div className="text-gray-400 whitespace-pre-wrap pl-2 mt-0.5">
                            {entry.response}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom: NOTES.md ─────────────────────────────── */}
      {notesFile && (
        <div className="border-t border-[#2a2a3e]" data-testid="sandbox-notes">
          <div className="flex items-center gap-2 bg-[#12121e] px-3 py-1.5 border-b border-[#2a2a3e]">
            <StickyNote className="w-3.5 h-3.5 text-blue-400" />
            <span className="text-xs font-medium text-gray-300">NOTES.md</span>
            <span className="text-[10px] text-gray-500">
              — scratch pad for your investigation notes
            </span>
          </div>
          <Editor
            height="120px"
            language="markdown"
            value={fileContents[notesFile.path] || ""}
            onChange={handleNotesChange}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 12,
              lineNumbers: "off",
              scrollBeyondLastLine: false,
              wordWrap: "on",
              tabSize: 2,
              automaticLayout: true,
              readOnly: submitted,
              padding: { top: 6, bottom: 6 },
            }}
          />
          {voiceEnabled && (
            <div className="flex items-center gap-3 px-3 py-2 bg-[#12121e] border-t border-[#2a2a3e]">
              <span className="text-xs text-gray-500 italic">or</span>
              <VoiceRecorder
                token={token}
                questionId={`${id}__notes`}
                sectionId={sectionId}
                onTranscription={(text) => {
                  const existing = fileContents[notesFile.path] || "";
                  const updated = existing ? `${existing}\n\n${text}` : text;
                  handleNotesChange(updated);
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
